import { Archive, Trash } from "@phosphor-icons/react";

import { Button } from "@/components/ui/button";
import { getStorySectionLabel } from "@/utils/storySections";

const formatTime = (value) => {
  try {
    return new Date(value).toLocaleString([], {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return value;
  }
};

const getDeskStatus = (story) => {
  if (story.content_type === "live") return "live on homepage";
  if (story.was_live) return "ended live stream";
  return story.platform;
};

export const NewsroomControlPanels = ({ archivedStories, deletingSlug, loading, onDelete, onRefresh, onRestore, restoringArchiveId, stories }) => (
  <section className="mt-8 grid gap-6 xl:grid-cols-[minmax(0,1fr)_360px]" data-testid="newsroom-control-panels">
    <div className="border border-[rgba(10,15,26,0.12)] bg-white p-6 sm:p-8" data-testid="newsroom-delete-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="eyebrow text-red-600" data-testid="newsroom-delete-overline">Colton control room</p>
          <h2 className="headline-lg mt-3" data-testid="newsroom-delete-title">Remove stories from the website and send them into the private archive vault.</h2>
        </div>
        <Button className="rounded-none border border-slate-950 bg-transparent px-4 py-3 text-xs font-bold uppercase tracking-[0.18em] text-slate-950 hover:bg-slate-950 hover:text-white" data-testid="newsroom-delete-refresh-button" onClick={onRefresh} type="button" variant="ghost">
          Refresh list
        </Button>
      </div>
      <div className="mt-6 space-y-3" data-testid="newsroom-delete-list">
        {loading ? <p className="text-sm text-slate-600" data-testid="newsroom-delete-loading">Loading story controls…</p> : null}
        {!loading && !stories.length ? <p className="text-sm text-slate-600" data-testid="newsroom-delete-empty">No stories are available to delete right now.</p> : null}
        {stories.map((story) => (
          <div key={story.slug} className="flex flex-col gap-4 border border-[rgba(10,15,26,0.08)] p-4 lg:flex-row lg:items-center lg:justify-between" data-testid={`newsroom-delete-item-${story.slug}`}>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-red-600" data-testid={`newsroom-delete-item-platform-${story.slug}`}>{getDeskStatus(story)} · {getStorySectionLabel(story.section)}</p>
              <p className="mt-2 font-['Outfit'] text-xl font-bold tracking-tight text-slate-950" data-testid={`newsroom-delete-item-title-${story.slug}`}>{story.headline}</p>
              <p className="mt-2 text-sm text-slate-600" data-testid={`newsroom-delete-item-meta-${story.slug}`}>{story.location} · {formatTime(story.published_at)}</p>
            </div>
            <Button className="rounded-none bg-[#D21404] px-4 py-4 text-xs font-bold uppercase tracking-[0.18em] text-white hover:bg-[#B31203]" data-testid={`newsroom-delete-item-button-${story.slug}`} disabled={deletingSlug === story.slug} onClick={() => onDelete(story.slug)} type="button">
              <Trash size={16} weight="bold" />
              {deletingSlug === story.slug ? "Archiving" : "Delete + Archive"}
            </Button>
          </div>
        ))}
      </div>
    </div>

    <aside className="border border-[rgba(10,15,26,0.12)] bg-slate-950 p-6 text-white sm:p-8" data-testid="newsroom-archive-panel">
      <div className="flex items-center gap-3">
        <Archive size={24} weight="fill" className="text-red-400" />
        <div>
          <p className="eyebrow text-red-400" data-testid="newsroom-archive-overline">Private archive vault</p>
          <h2 className="mt-2 font-['Outfit'] text-2xl font-bold tracking-tight text-white" data-testid="newsroom-archive-title">Removed stories stay here for Colton only.</h2>
        </div>
      </div>
      <div className="mt-6 space-y-3" data-testid="newsroom-archive-list">
        {!archivedStories.length ? <p className="text-sm text-slate-300" data-testid="newsroom-archive-empty">Archived stories will appear here after they’re removed from the site.</p> : null}
        {archivedStories.map((entry) => (
          <div key={entry.archive_id} className="border border-white/10 bg-white/5 p-4" data-testid={`newsroom-archive-item-${entry.slug}`}>
            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-red-300">{entry.platform} · archived {formatTime(entry.archived_at)}</p>
            <p className="mt-2 font-['Outfit'] text-lg font-bold tracking-tight text-white">{entry.headline}</p>
            <p className="mt-2 text-sm text-slate-300">Removed by {entry.archived_by_email}</p>
            <Button className="mt-4 w-full rounded-none bg-white px-4 py-3 text-xs font-bold uppercase tracking-[0.18em] text-slate-950 hover:bg-slate-100" data-testid={`newsroom-archive-restore-button-${entry.archive_id}`} disabled={restoringArchiveId === entry.archive_id} onClick={() => onRestore(entry.archive_id)} type="button">
              {restoringArchiveId === entry.archive_id ? "Restoring" : "Restore Live Story"}
            </Button>
          </div>
        ))}
      </div>
    </aside>
  </section>
);